<template>
  <div class="donate-colected">
    <div class="donate-colected__head">
      <span>{{ $t('donate.collected') }}</span>
      <span class="goal">
        {{ $t('donate.goal') }}
        <template v-if="project.goals.length > 1">
          {{ finishedAndActiveGoals.length }}/{{ project.goals.length }}

          <PagesProjectGoalsModal
            :goals="project.goals"
            :locale="locale"
            :finished="finished"
            @open-donate-modal="$emit('open-donate-modal')"
          />
        </template>
      </span>
    </div>
    <div class="donate-colected__sum">
      <p class="--current">${{ donationCurrent }}</p>
      <p class="--goal">${{ currentGoalSum }}</p>
    </div>
    <div class="donate-colected__line" :class="{ '--finished': finished }">
      <span
        class="donate-colected__line --active"
        :style="{
          width: `${
            finished ? Math.min(100, finishedPercent) : donationPercent
          }%`,
        }"
      />
      <span v-if="!finished" class="donate-colected__check-icon">
        <TransitionGroup name="list">
          <svgo-goal-check
            v-for="count in finishedAndActiveGoals.length - 1"
            :key="count"
            class="icon"
          />
        </TransitionGroup>
      </span>
    </div>
    <div class="donate-colected__participants">
      <div class="--participants">
        <svgo-participants width="20px" height="20px" class="icon" />
        <span>{{ donationState.countParticipants }}</span>
      </div>
      <div class="--finish-percent">
        <svgo-finish width="20px" height="20px" class="icon" />
        <span>{{ finishedPercent }}%</span>
      </div>
    </div>
  </div>
</template>

<script setup>
defineEmits(['open-donate-modal']);

const props = defineProps({
  project: {
    type: Object,
    default: () => ({}),
  },
  locale: {
    type: String,
    default: 'en',
  },
  finished: {
    type: Boolean,
    default: false,
  },
});

const finishedAndActiveGoals = computed(() => {
  return props.project.goals.filter(
    (goal) => goal.state === 'finished' || goal.state === 'active',
  );
});

const donationCurrent = computed(() =>
  getSum(donationState.value.donationCurrent),
);

const currentGoalSum = computed(() =>
  getSum(
    finishedAndActiveGoals.value.reduce((sum, goal) => sum + goal.target, 0),
  ),
);

const donationState = computed(() => props.project.donationState || {});

const donationPercent = computed(() => {
  const totalTarget = finishedAndActiveGoals.value.reduce(
    (sum, goal) => sum + goal.target,
    0,
  );

  if (totalTarget === 0) return 0;

  const weightPerGoal = 100 / finishedAndActiveGoals.value.length;

  let totalProgress = 0;

  for (const goal of finishedAndActiveGoals.value) {
    if (goal.state === 'finished') {
      totalProgress += weightPerGoal;
    } else {
      const goalProgress = Math.min(goal.current / goal.target, 1);
      totalProgress += goalProgress * weightPerGoal;
    }
  }

  return Math.round(totalProgress * 1000) / 1000;
});

const finishedPercent = computed(() => {
  const goalSum = finishedAndActiveGoals.value.reduce(
    (sum, goal) => sum + goal.target,
    0,
  );
  return ((donationState.value.donationCurrent / goalSum) * 100).toFixed(1);
});

function getSum(sumWithCents) {
  const cents = sumWithCents?.toString().slice(-2);
  const sum = sumWithCents?.toString().slice(0, -2);
  return sumWithCents
    ? Number(`${sum}.${cents}`).toLocaleString(props.locale)
    : 0;
}
</script>

<style lang="scss">
@use '@styles/components/pages/project/donateColected.scss' as *;
</style>
